import { Coordinate } from '../../api/apiTypes'
import { calculateDistance } from '../../utils/distanceUtils'

/** Maximum relative difference in track length to consider layouts compatible (20%). */
const MAX_LENGTH_DEVIATION = 0.2
/** Maximum distance in meters between finish line positions for compatible layouts. */
const MAX_FINISH_LINE_DISTANCE_METERS = 300

/**
 * Get the reference position for a finish line (midpoint of 2-point line, or the single point).
 * Returns undefined when the finish line is empty or missing.
 */
export function getFinishLinePosition(
  finishLine?: Coordinate[]
): Coordinate | undefined {
  if (!finishLine || finishLine.length === 0) return undefined
  if (finishLine.length === 1) return finishLine[0]
  return {
    latitude: (finishLine[0].latitude + finishLine[1].latitude) / 2,
    longitude: (finishLine[0].longitude + finishLine[1].longitude) / 2,
  }
}

/**
 * Check whether two track layouts at the same venue are compatible for comparison.
 * Uses two independent signals:
 * 1. Track length — within {@link MAX_LENGTH_DEVIATION} of the longer track
 * 2. Finish line position — within {@link MAX_FINISH_LINE_DISTANCE_METERS} of each other
 * When a signal is unknown for either track, it is skipped (benefit of the doubt).
 */
export function areTracksCompatible(
  lengthA: number | undefined,
  lengthB: number | undefined,
  positionA?: Coordinate,
  positionB?: Coordinate
): boolean {
  // Length check
  if (lengthA != null && lengthB != null) {
    const longer = Math.max(lengthA, lengthB)
    if (
      longer > 0 &&
      Math.abs(lengthA - lengthB) / longer > MAX_LENGTH_DEVIATION
    ) {
      return false
    }
  }
  // Finish line position check (different start/finish → different layout)
  if (positionA && positionB) {
    const dist = calculateDistance(
      positionA.latitude,
      positionA.longitude,
      positionB.latitude,
      positionB.longitude
    )
    if (dist > MAX_FINISH_LINE_DISTANCE_METERS) return false
  }
  return true
}
